// AI-powered chat input for continuing conversation on existing issues
// Messages are persisted to issue_conversations and appear in the main conversation list
import { useState, useCallback } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Loader2, X, MessageSquare } from 'lucide-react'
import { ChatInput } from '@/components/chat/ChatInput'
import { buildCustomerProfileContext } from '@/lib/customer-context-builder'
import { buildFileContext } from '@/lib/file-context-builder'
import { uploadIssueFiles } from '@/lib/issue-file-upload'
import { executeAgent } from '@/modules/agents/agent-client'
import { getAgentForFeature } from '@/hooks/useOrgSettings'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { useQueryClient } from '@tanstack/react-query'
import type { Customer, CustomerEntity, CustomerUser } from '@/types/customer'

interface IssueDetailChatProps {
  issueId: string
  issueDescription: string
  d365Version: string
  d365Module: string
  existingConversations: { role: string; content: string }[]
  customer?: Customer | null
  entity?: CustomerEntity | null
  reportedBy?: CustomerUser | null
  organizationId: string
  onClose: () => void
}

export function IssueDetailChat({
  issueId, issueDescription, d365Version, d365Module,
  existingConversations, customer, entity, reportedBy,
  organizationId, onClose,
}: IssueDetailChatProps): JSX.Element {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const agentId = getAgentForFeature(
    'issue_recorder',
    import.meta.env.VITE_KAYCOO_ISSUE_RECORDER_AGENT_ID || 'd365a1b2-1111-5000-9000-15511e0ec07d',
  )

  const handleSend = useCallback(async (content: string, files?: File[]) => {
    if (!user) return
    setIsProcessing(true)
    setError(null)

    try {
      let enrichedContent = content
      if (files && files.length > 0) {
        const fileContext = await buildFileContext(files)
        if (fileContext) enrichedContent = content ? `${content}\n\n${fileContext}` : fileContext
        const docIds = await uploadIssueFiles(files, customer?.id ?? null, organizationId, issueId)
        if (docIds.length > 0) {
          queryClient.invalidateQueries({ queryKey: ['issue-detail', issueId, 'attachments'] })
        }
      }

      // Save user message to conversation immediately
      await supabase.from('issue_conversations').insert({
        issue_id: issueId, organization_id: organizationId,
        user_id: user.id, role: 'user', content: enrichedContent,
        is_internal: false, metadata: {},
      } as never)
      queryClient.invalidateQueries({ queryKey: ['issue-detail', issueId, 'conversations'] })

      // Build context and call agent
      const profileCtx = buildCustomerProfileContext(customer ?? null, entity ?? null, reportedBy ?? null)
      const recentHistory = existingConversations.slice(-10)
        .map((m) => `${m.role}: ${m.content}`).join('\n')

      const prompt = [
        profileCtx,
        `[EXISTING ISSUE]\nTitle: ${issueDescription}\nD365 Version: ${d365Version}\nModule: ${d365Module}`,
        recentHistory ? `[CONVERSATION HISTORY]\n${recentHistory}` : '',
        `user: ${content}`,
      ].filter(Boolean).join('\n\n')

      const result = await executeAgent(agentId, { input: prompt })
      const response = result.response || result.result || result.output || ''

      if (response) {
        await supabase.from('issue_conversations').insert({
          issue_id: issueId, organization_id: organizationId,
          user_id: null, role: 'assistant', content: response,
          is_internal: false, metadata: {},
        } as never)
        queryClient.invalidateQueries({ queryKey: ['issue-detail', issueId, 'conversations'] })
      }
    } catch (err) {
      setError((err as Error).message || 'Failed to get response')
    } finally {
      setIsProcessing(false)
    }
  }, [user, agentId, issueId, organizationId, customer, entity, reportedBy,
      issueDescription, d365Version, d365Module, existingConversations, queryClient])

  return (
    <Card className="border-kaycoo-teal-light bg-kaycoo-white">
      <div className="flex items-center justify-between border-b border-kaycoo-white-3 px-3 py-2">
        <div className="flex items-center gap-2 text-sm font-medium text-kaycoo-teal-dark">
          <MessageSquare className="h-4 w-4" />
          AI Chat Active
          {isProcessing && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}
          className="h-6 w-6 p-0 text-kaycoo-charcoal-2 hover:text-kaycoo-charcoal">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
      {error && (
        <div className="mx-3 mt-2 rounded bg-kaycoo-burgundy-pale px-3 py-1.5 text-xs text-kaycoo-burgundy-dark">
          {error}
        </div>
      )}
      <ChatInput onSendMessage={handleSend} disabled={isProcessing}
        placeholder="Ask the AI agent..." allowFileUpload />
    </Card>
  )
}
